/**
 * Runtime deployment configuration, loaded from /config.json before the app
 * boots (roc-151q.6).
 *
 * This replaces build-time `import.meta.env.VITE_*` inlining so one built
 * artifact serves every deployment: the SWA workflow, a container entrypoint,
 * or the Vite dev server (which synthesizes /config.json from .env — see
 * vite.config.ts) each supply their own config.json; the bundle itself is
 * deployment-agnostic.
 *
 * The failure philosophy of the old utils/env.ts requireEnv carries over
 * (roc-151q.19): a deployment missing any required value cannot run, and the
 * only useful behaviour is to say so immediately and name the missing field.
 * Plausible fallbacks ('common', localhost URLs) are worse than loud failure —
 * they produce an app that signs into the wrong tenant or quietly calls a
 * backend that isn't there.
 *
 * What is required is now per-mode (roc-151q.37). The Entra fields have no
 * value on a deployment with no app registration, so requiring them there would
 * force exactly the placeholder values the loud failure exists to prevent. The
 * philosophy is unchanged; the required set is a function of authMode.
 */

/**
 * How this deployment signs users in (roc-151q.37). Mirrors the backend's
 * AUTH_MODE, and must match it: the backend only mounts /api/auth/login under
 * AUTH_MODE=local, and only accepts Entra-issued tokens under entra.
 *
 *   entra — Entra ID via MSAL. Needs an app registration, so it needs
 *           azureClientId / azureTenantId / apiScope.
 *   local — usernames and passwords in the deployment's own database, for a
 *           stamp with no customer IdP to point at. Loads no MSAL at all.
 */
export type AuthMode = 'entra' | 'local';

export const AUTH_MODES: readonly AuthMode[] = ['entra', 'local'];

/** Absent authMode means entra, matching the backend's AUTH_MODE default, so an
 *  existing deployment's config.json keeps meaning exactly what it meant. */
export const DEFAULT_AUTH_MODE: AuthMode = 'entra';

export interface RuntimeConfig {
  /** ddb-web-backend base URL, e.g. https://roc-web-backend.azurewebsites.net */
  apiUrl: string;
  /** Which sign-in stack this deployment runs. */
  authMode: AuthMode;
  /** Entra SPA app registration (DEX Mission Control) client id. entra only. */
  azureClientId: string | null;
  /** Entra tenant id. entra only. */
  azureTenantId: string | null;
  /** Backend API scope requested for access tokens. entra only. */
  apiScope: string | null;
  /** Client-side Google Maps key (referrer-restricted, not a secret). */
  googleMapsApiKey: string;
  /** Cesium Ion access token for 3D terrain/imagery. */
  cesiumIonToken: string;
  /**
   * Base URL the SRTM elevation tiles are fetched from (roc-151q.38).
   *
   * Absolute (`https://<account>.z19.web.core.windows.net`) or same-origin
   * (`/srtm-tiles`, which a stamp's Caddy reverse-proxies). The dataset is the
   * global 1-arcsecond set — ~24k tiles, hundreds of GB — so a deployment
   * points at a shared copy rather than owning one; what matters is that the
   * pointer is a per-deployment setting instead of a hostname compiled into
   * ElevationModel, and that a stamp can keep it same-origin so no storage
   * endpoint it does not own appears in its config at all.
   */
  terrainBaseUrl: string;
  /** PostHog project key; null disables analytics entirely. */
  posthogKey: string | null;
  /** LAN SITL renderer control URL (sim environments only); null hides nothing
   *  but makes the navbar renderer-restart action report it is unconfigured. */
  simRendererUrl: string | null;
}

/** Required whatever the sign-in stack. */
const REQUIRED_FIELDS = [
  'apiUrl',
  'googleMapsApiKey',
  'cesiumIonToken',
  'terrainBaseUrl',
] as const;

/** Required only under authMode=entra. Requiring these unconditionally is what
 *  made an IdP-less deployment impossible: there is no app registration to take
 *  them from, and inventing placeholders would defeat the loud failure they
 *  exist for. */
const ENTRA_REQUIRED_FIELDS = ['azureClientId', 'azureTenantId', 'apiScope'] as const;

let loaded: RuntimeConfig | null = null;

function readAuthMode(cfg: Record<string, unknown>): AuthMode {
  const raw = cfg.authMode;
  if (raw === undefined || raw === null || raw === '') return DEFAULT_AUTH_MODE;
  if (typeof raw !== 'string' || !(AUTH_MODES as readonly string[]).includes(raw)) {
    throw new Error(
      `runtime config: authMode must be one of ${AUTH_MODES.join(', ')} ` +
      `(got ${JSON.stringify(raw)}). It must also match the backend's AUTH_MODE.`,
    );
  }
  return raw as AuthMode;
}

/** An entra-only field, normalized to null under local mode. */
function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Validate and install a config object. Exported for the boot loader and for
 * unit-test setup (tests/unit/setup.ts installs a fixture config).
 */
export function initRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('runtime config: /config.json did not parse to an object');
  }
  const cfg = raw as Record<string, unknown>;
  const authMode = readAuthMode(cfg);
  const required: readonly string[] =
    authMode === 'entra' ? [...REQUIRED_FIELDS, ...ENTRA_REQUIRED_FIELDS] : REQUIRED_FIELDS;
  const missing = required.filter(
    (f) => typeof cfg[f] !== 'string' || cfg[f] === '',
  );
  if (missing.length > 0) {
    throw new Error(
      `runtime config: missing required field(s) ${missing.join(', ')} ` +
      `for authMode=${authMode}. ` +
      'This deployment cannot run — fix the config.json served at the site root ' +
      '(generated by scripts/generate-config.mjs or the dev-server plugin).',
    );
  }
  loaded = {
    apiUrl: cfg.apiUrl as string,
    authMode,
    azureClientId: optionalString(cfg.azureClientId),
    azureTenantId: optionalString(cfg.azureTenantId),
    apiScope: optionalString(cfg.apiScope),
    googleMapsApiKey: cfg.googleMapsApiKey as string,
    cesiumIonToken: cfg.cesiumIonToken as string,
    terrainBaseUrl: (cfg.terrainBaseUrl as string).replace(/\/+$/, ''),
    posthogKey: optionalString(cfg.posthogKey),
    simRendererUrl: optionalString(cfg.simRendererUrl),
  };
  return loaded;
}

/**
 * Fetch /config.json and install it. Called exactly once, by main.ts, before
 * any module that reads config is evaluated.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // no-store: a redeploy changes config.json in place; a cached copy from a
  // previous deployment must never outlive the bundle it was built for.
  //
  // BOUNDED (roc-pvnv). This is the first await of the boot: main.ts renders
  // the config-failure shell if it REJECTS, and nothing at all if it never
  // settles — the operator gets index.html's placeholder, permanently, with no
  // console error and no failed request to see. A tiny same-origin JSON file
  // that has not arrived in 15s is not arriving.
  const res = await fetch('/config.json', {
    cache: 'no-store',
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) {
    throw new Error(
      `runtime config: GET /config.json returned ${res.status}. ` +
      'The deployment did not publish its config — see scripts/generate-config.mjs.',
    );
  }
  return initRuntimeConfig(await res.json());
}

/**
 * The installed config. Throws until loadRuntimeConfig()/initRuntimeConfig()
 * has run — module-eval reads are only legal in code loaded via the
 * post-config dynamic import in main.ts (or after test setup).
 */
export function runtimeConfig(): RuntimeConfig {
  if (loaded === null) {
    throw new Error(
      'runtime config read before it was loaded — code that reads config at ' +
      'module scope must be imported via src/bootstrap.ts, not from main.ts.',
    );
  }
  return loaded;
}

/**
 * The Entra settings, non-null. Throws under authMode=local: reaching for them
 * there is a caller bug, not a misconfigured deployment. Under entra the
 * validation above has already established all three are set, so this narrows
 * without a second round of "or throw" at every use site.
 */
export function entraConfig(): { clientId: string; tenantId: string; apiScope: string } {
  const cfg = runtimeConfig();
  if (cfg.authMode !== 'entra') {
    throw new Error(
      `runtime config: Entra settings read under authMode=${cfg.authMode}. ` +
      'This deployment has no app registration — the caller must branch on authMode.',
    );
  }
  return {
    clientId: cfg.azureClientId as string,
    tenantId: cfg.azureTenantId as string,
    apiScope: cfg.apiScope as string,
  };
}
